import { useEffect, useState } from 'react';
import core, { App, GQL } from '@gin-betting/core';
import subDays from 'date-fns/subDays';
import subHours from 'date-fns/subHours';
import { createSelector } from 'reselect';

import { LIVE_SPORTEVENT_STATUSES } from 'app-constants';
import { useMemoSelector } from 'hooks';
import type { State } from 'types';
import { selectIsOddHistoryEnabled, selectOddFormat } from '../../selectors';
import { selectEntitiesData } from './selectors';

const {
    utils: { splitOddPath, getIdsFromOddId, format },
    appServiceLocator,
} = core;

const selectProps = createSelector(
    selectOddFormat,
    selectIsOddHistoryEnabled,
    selectEntitiesData,
    (oddFormat, isOddHistoryEnabled, entitiesData) => ({
        oddFormat,
        isOddHistoryEnabled,
        entitiesData,
    })
);

interface Input {
    oddId: string;
}

interface Output {
    marketName: string;
    oddName: string;
    oddFormat: App.OddFormat;
    dynamics: number[];
}

const useOddHistory = ({ oddId }: Input): Output => {
    const [dynamics, setDynamics] = useState<number[]>([]);

    const { matchId, marketId } = getIdsFromOddId(oddId)!;

    const { entitiesData, isOddHistoryEnabled, oddFormat } = useMemoSelector(
        (s: State, p) => selectProps(s, p),
        {
            oddId,
            matchId,
            marketId,
        }
    );

    const { marketName, oddName, matchStatus } = entitiesData;

    useEffect(() => {
        let isUnmount = false;

        if (isOddHistoryEnabled && matchStatus) {
            const variables = getVariables(oddId, matchStatus);

            const apiClient = appServiceLocator.get('apiClient');

            apiClient
                .makeRequest({
                    method: 'getOddDynamicsByInterval',
                    variables,
                })
                .then(({ data }) => {
                    if (!data || isUnmount) return;

                    const nextDynamics = data.oddDynamicsByInterval.dynamics
                        .slice(-5)
                        .map(obj => obj.value);

                    setDynamics(nextDynamics);
                })
                .catch(err => {
                    console.error(err);
                });
        }

        return () => {
            isUnmount = true;
        };
    }, [matchStatus, oddId, isOddHistoryEnabled]);

    return { dynamics, marketName, oddName, oddFormat };
};

function getVariables(
    oddPath: string,
    sportEventStatus: GQL.SportEventStatus
): GQL.GetOddDynamicsByIntervalQueryVariables {
    const { marketId, matchId: sportEventId, oddId } = splitOddPath(oddPath)!;

    const date = new Date();

    const isHours = LIVE_SPORTEVENT_STATUSES.includes(sportEventStatus);

    const from = format(isHours ? subHours(date, 2) : subDays(date, 3));

    return {
        marketId,
        sportEventId,
        oddId,
        pointCount: 5,
        from,
    };
}

export default useOddHistory;
